<template>
  <div 
    class="slideshow-container" 
    :class="{ 'full-height': fullHeight }"
    :style="!fullHeight ? { height: height } : {}"
    @mouseenter="pauseOnHover && pauseAutoplay()"
    @mouseleave="pauseOnHover && resumeAutoplay()"
  >
    <div v-if="!slides || slides.length === 0" class="empty-slideshow">
      <p>No slides to display</p>
    </div>
    <div 
      v-else
      v-for="(slide, index) in filteredSlides" 
      :key="slide.id || index"
      class="slide"
      :class="{ 
        'active': currentIndex === index,
        'fade': transitionType === 'fade',
        'slide-left': transitionType === 'slide' && currentIndex === index,
        'slide-right': transitionType === 'slide' && currentIndex !== index
      }"
      :style="{ 
        backgroundImage: slide.campaign_image ? `url(${slide.campaign_image})` : 'none',
        transform: transitionType === 'slide' ? 
          (currentIndex === index ? 'translateX(0)' : 'translateX(100%)') : 'none'
      }"
    >
      <!--<div class="slide-content" :style="`background-color:${slide.color}22`" >-->
      <div class="slide-content" :style="`background-color:#00000000`">
        <div class="slide-content-wrapper">
          <div class="slide-text with-shadow">
            <div v-if="slide.logo_image" class="logo-container">
              <img :src="slide.logo_image" :alt="slide.primary_title" class="logo-image">
            </div>
            <h1 class="primary-title">{{ slide.primary_title }}</h1>
            <!-- <h2 class="secondary-title">{{ slide.secondary_title }}</h2> -->
            <p class="subtitle">{{ slide.subtitle }}</p>
            <a 
              v-if="slide.is_btn_active && slide?.btn_text?.length > 0" 
              :href="slide.btn_link" 
              class="action-button" 
              :style="{ backgroundColor: slide.color }"
              target="_blank"
            >
              {{ slide.btn_text }}
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Navigation buttons -->
    <button 
      class="prev-button" 
      @click="prevSlide" 
      v-if="filteredSlides.length > 1 && showNavButtons"
    >&lt;</button>
    <button 
      class="next-button" 
      @click="nextSlide" 
      v-if="filteredSlides.length > 1 && showNavButtons"
    >&gt;</button>

    <!-- Indicators -->
    <div class="indicators" v-if="filteredSlides.length > 1 && showIndicators">
      <button 
        v-for="(slide, index) in filteredSlides" 
        :key="`indicator-${slide.id || index}`"
        class="indicator"
        :class="{ active: currentIndex === index }"
        @click="goToSlide(index)"
      ></button>
    </div>
    
    <!-- Pause indicator -->
    <div class="pause-indicator" v-if="isPaused && props.autoplay">
      <span class="pause-icon"></span>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue';

const props = defineProps({
  slides: {
    type: Array,
    default: () => []
  },
  transitionType: {
    type: String,
    default: 'fade',
    validator: (value) => ['fade', 'slide'].includes(value)
  },
  autoplay: {
    type: Boolean,
    default: true
  },
  interval: {
    type: Number,
    default: 5000
  },
  height: {
    type: String,
    default: '500px'
  },
  showIndicators: {
    type: Boolean,
    default: true
  },
  initialSlide: {
    type: Number,
    default: 0
  },
  filterByDate: {
    type: Boolean,
    default: true
  },
  fullHeight: {
    type: Boolean,
    default: false
  },
  pauseOnHover: {
    type: Boolean,
    default: true
  },
  showNavButtons: {
    type: Boolean,
    default: true
  }
});

const currentIndex = ref(props.initialSlide);
let autoplayInterval = null;
const isPaused = ref(false);

const shadowpacity = ref(0.4);


// Check if slide is within campaign date range
const validSlides = computed(() => {
  if (!props.slides || !Array.isArray(props.slides)) {
    return [];
  }
  
  const now = new Date();
  return props.slides.filter(slide => {
    if (!slide.start_date || !slide.end_date) {
      return true; // Include slides without date restrictions
    }
    const startDate = new Date(slide.start_date);
    const endDate = new Date(slide.end_date);
    return now >= startDate && now <= endDate;
  });
});

// Filtered slides - either all slides or only valid ones based on date range
const filteredSlides = computed(() => {
  return props.slides;
});

// Watch for changes in filteredSlides
watch(filteredSlides, (newFilteredSlides) => {
  if (newFilteredSlides.length === 0) {
    return;
  }
  
  // Ensure currentIndex is within valid range
  if (currentIndex.value >= newFilteredSlides.length) {
    currentIndex.value = 0;
  }
}, { immediate: true });

// Methods for navigation
function nextSlide() {
  if (filteredSlides.value.length === 0) return;
  currentIndex.value = (currentIndex.value + 1) % filteredSlides.value.length;
  resetAutoplay();
}

function prevSlide() {
  if (filteredSlides.value.length === 0) return;
  currentIndex.value = (currentIndex.value - 1 + filteredSlides.value.length) % filteredSlides.value.length;
  resetAutoplay();
}

function goToSlide(index) {
  currentIndex.value = index;
  resetAutoplay();
}

function pauseAutoplay() {
  if (props.autoplay) {
    clearInterval(autoplayInterval);
    autoplayInterval = null;
    isPaused.value = true;
  }
}

function resumeAutoplay() {
  if (props.autoplay && isPaused.value) {
    isPaused.value = false;
    if (filteredSlides.value.length > 1) {
      startAutoplay();
    }
  }
}

function resetAutoplay() {
  if (props.autoplay && !isPaused.value) {
    clearInterval(autoplayInterval);
    autoplayInterval = null;
    startAutoplay();
  }
}

function startAutoplay() {
  // Clear any existing intervals first
  if (autoplayInterval) {
    clearInterval(autoplayInterval);
    autoplayInterval = null;
  }
  
  if (props.autoplay && filteredSlides.value.length > 1 && !isPaused.value) {
    autoplayInterval = setInterval(() => {
      nextSlide();
    }, props.interval);
  }
}

function handleKeydown(event) {
  if (!props.showNavButtons) return; // Respect the showNavButtons prop
  
  if (event.key === 'ArrowLeft') {
    prevSlide();
  } else if (event.key === 'ArrowRight') {
    nextSlide();
  }
}

/**
 * Component lifecycle hooks
 * - On mount: starts autoplay and adds keyboard event listeners
 * - On unmount: cleans up intervals and event listeners
 * 
 * Note: Navigation buttons can be toggled with the showNavButtons prop
 * Keyboard navigation (arrow keys) is also tied to this prop setting
 */
onMounted(() => {
  startAutoplay();
  window.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  if (autoplayInterval) {
    clearInterval(autoplayInterval);
  }
  window.removeEventListener('keydown', handleKeydown);
});
</script>

<style scoped>
.slideshow-container {
  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.slideshow-container.full-height {
  height: 100vh;
  border-radius: 0;
  box-shadow: none;
}

.slide {
  position: absolute;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  transition: all 0.5s ease;
  opacity: 0;
  z-index: 0;
}

/* Fade transition */
.slide.fade {
  transition: opacity 0.5s ease-in-out;
}

.slide.fade.active {
  opacity: 1;
  z-index: 1;
}

/* Slide transition */
.slide.slide-left {
  transform: translateX(0);
  opacity: 1;
  z-index: 1;
}

.slide.slide-right {
  transform: translateX(100%);
  opacity: 1;
  z-index: 0;
}

.slide-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  padding: 2rem;
  color: white;
}

.full-height .slide-content {
  align-items: center;
  padding: 0;
}

.slide-content-wrapper {
  max-width: 600px;
  padding: 2rem;
  border-radius: 8px;
}

.full-height .slide-content-wrapper {
  padding: 3rem;
  max-width: 800px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
}

.logo-container {
  position: relative;
  bottom: -20px;
  /* background-color: green; */
}

.logo-image {
  max-height: 160px;
  /* max-width: 200px; */
  object-fit: contain;
}

.primary-title {
  font-size: 50px;
  /* font-size: 2.5rem; */
  line-height: 60px;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.secondary-title {
  font-size: 1.8rem;
  font-weight: 500;
  margin-bottom: 1rem;
}

.subtitle {
  font-size: 30px;
  /* font-size: 1rem; */
  margin-bottom: 1.5rem;
  line-height: 40px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
}

.action-button {
  display: inline-block;
  padding: 1.55rem 3.3rem;
  border-radius: 40px;
  color: white;
  text-decoration: none;
  font-weight: 600;
  font-size: 24px;
  /* text-transform: uppercase; */
  letter-spacing: 1px;
  transition: all 0.3s ease;
}

.action-button:hover {
  opacity: 0.9;
  transform: translateY(-2px);
}

.prev-button,
.next-button {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  font-size: 1.2rem;
  cursor: pointer;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.prev-button {
  left: 20px;
}

.next-button {
  right: 20px;
}

.prev-button:hover,
.next-button:hover {
  background-color: rgba(0, 0, 0, 0.7);
}

.indicators {
  position: absolute;
  bottom: 60px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 10px;
  z-index: 10;
}

.indicator {
  width: 25px;
  height: 25px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.2);
  border: 1px solid white;
  cursor: pointer;
  transition: all 0.3s ease;
}

.indicator.active {
  background-color: white;
  /* transform: scale(1.2); */
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .slide-content-wrapper {
    padding: 1.5rem;
  }
  
  .primary-title {
    font-size: 1.8rem;
  }
  
  .secondary-title {
    font-size: 1.2rem;
  }
  
  .subtitle {
    -webkit-line-clamp: 2;
    line-clamp: 2;
    font-size: 0.9rem;
  }
  
  .full-height .slide-content-wrapper {
    padding: 2rem;
    max-width: 90%;
  }
}

@media (max-width: 480px) {
  .slide-content-wrapper {
    padding: 1rem;
  }
  
  .primary-title {
    font-size: 1.5rem;
  }
  
  .secondary-title {
    font-size: 1rem;
  }
  
  .action-button {
    padding: 0.5rem 1rem;
    font-size: 0.9rem;
  }
  
  .full-height .slide-content-wrapper {
    padding: 1.5rem;
    max-width: 100%;
  }
  
  .full-height .indicators {
    bottom: 10px;
  }
}

/* Empty state */
.empty-slideshow {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  background-color: #f5f5f5;
  color: #666;
  font-size: 1.2rem;
  border-radius: 8px;
}

/* Pause indicator */
.pause-indicator {
  position: absolute;
  top: 20px;
  right: 20px;
  background-color: rgba(0, 0, 0, 0.5);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
  opacity: 0;
  animation: fadeIn 0.3s forwards;
}

.pause-icon {
  position: relative;
  width: 16px;
  height: 16px;
}

.pause-icon:before,
.pause-icon:after {
  content: '';
  position: absolute;
  background-color: white;
  width: 5px;
  height: 16px;
  border-radius: 2px;
}

.pause-icon:before {
  left: 2px;
}

.pause-icon:after {
  right: 2px;
}


.with-shadow {
  /* Mimicking text-shadow: 2px 2px 5px rgba(0,0,0,0.5); */
  filter: drop-shadow(1px 1px 1px rgba(0, 0, 0, v-bind(shadowpacity)));  
  text-shadow: 1px 1px 1px rgba(0, 0, 0, v-bind(shadowpacity));
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 0.7; }
}
</style>